import { Component, createContext, PropsWithChildren, ReactNode } from 'react';
import { connect, InferableComponentEnhancerWithProps } from 'react-redux';
import { Dispatch } from 'redux';
import { createTheme, GlobalStyles, Theme, ThemeProvider as MuiThemeProvider, PaletteMode } from '@mui/material';
import { blue, common, orange, red } from '@mui/material/colors';
import type { PayloadAction } from '@reduxjs/toolkit';

import { uiToolkit } from '/store/toolkits';
import type { RootState } from '/store';
import { setThemeModeStylingMetadata } from '/helpers/themeModeStylingMetadataSettingHelper';
import { preferredThemeModeLocalStorageKey } from '/constants/themeConstants';

import {
  IThemeModeContext,
  IThemeProviderDispatchProps,
  IThemeProviderState,
  IThemeProviderStateProps,
} from './models';

const inputLabelOverflowHeight = 5;
const globalCustomStyles = {
  '*': {
    '-webkit-tap-highlight-color': 'transparent',
  },
};
const cardPadding = 15;
const commonWhite: string = common.white;
const palettePrimary = {
  main: blue[700],
};
const paletteSecondary = {
  main: '#f50057',
  dark: '#c51162',
};

const sandboxColor = orange[500];
const liveColor = red[500];

const defaultBackgroundColor = '#fafafa';

export const disabledOpacity = 0.38;

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: palettePrimary,
    secondary: paletteSecondary,
    background: {
      paper: commonWhite,
      default: defaultBackgroundColor,
    },
    promotion: {
      sandbox: sandboxColor,
      live: liveColor,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          overflow: 'unset',
          backgroundImage: 'none',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: cardPadding,
          '.MuiCardHeader-action': {
            margin: 0,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: cardPadding,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          textTransform: 'inherit',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectLabel: {
          margin: 0,
        },
        displayedRows: {
          margin: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          '&+.MuiDialogContent-root': {
            paddingTop: `${inputLabelOverflowHeight}px !important`,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            pointerEvents: 'auto',
          },
        },
      },
    },
  },
});

export const textSecondaryDarkThemeColor = 'rgba(255, 255, 255, .7)';
export const textDisabledDarkThemeColor = 'rgba(255, 255, 255, .5)';
export const actionFocusDarkThemeColor = 'rgba(255, 255, 255, 0.12)';

export const darkPaperBackground = '#303030';
export const darkDefaultBackground = '#1c2127';
export const selectedCheckColor = '#008000';

export const darkTheme: Theme = createTheme({
  ...lightTheme,
  // overriding the light theme with the next palette:
  palette: {
    primary: palettePrimary,
    secondary: paletteSecondary,
    error: {
      main: red.A200,
    },
    mode: 'dark',
    text: {
      primary: commonWhite,
      secondary: textSecondaryDarkThemeColor,
      disabled: textDisabledDarkThemeColor,
    },
    background: {
      paper: darkPaperBackground,
      default: darkDefaultBackground,
    },
    action: {
      active: commonWhite,
      hover: 'rgba(255, 255, 255, 0.05)',
      hoverOpacity: 0.08,
      selected: 'rgba(255, 255, 255, 0.13)',
      selectedOpacity: 0.16,
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledOpacity,
      disabledBackground: actionFocusDarkThemeColor,
      focus: actionFocusDarkThemeColor,
      focusOpacity: 0.12,
      activatedOpacity: 0.24,
    },
    promotion: {
      sandbox: sandboxColor,
      live: liveColor,
    },
  },

  components: {
    ...lightTheme.components,
    MuiTypography: {
      styleOverrides: {
        body1: {
          color: commonWhite,
        },
      },
    },
    MuiOutlinedInput: {
      ...lightTheme.components?.MuiOutlinedInput,
      styleOverrides: {
        root: {
          '&.Mui-focused $notchedOutline': {
            borderColor: commonWhite,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: commonWhite,
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: () => ({
          backgroundColor: 'rgb(60, 60, 60)',
        }),
      },
    },
  },
});

const getInitialThemeMode = (): PaletteMode => {
  const targetThemeMode = localStorage.getItem(preferredThemeModeLocalStorageKey) as PaletteMode | null;
  const themeModeValues: PaletteMode[] = ['light', 'dark'];

  return !targetThemeMode || !themeModeValues.includes(targetThemeMode) ? 'light' : targetThemeMode;
};

const initialThemeModeContext = {
  currentThemeMode: getInitialThemeMode(),
  themesAvailable: {
    light: lightTheme,
    dark: darkTheme,
  },
  getCurrentTheme() {
    return this.themesAvailable[this.currentThemeMode];
  },
};

export const ThemeModeContext = createContext<IThemeModeContext>(initialThemeModeContext as IThemeModeContext);

export class ThemeProvider extends Component<
  PropsWithChildren<IThemeProviderStateProps & IThemeProviderDispatchProps>,
  IThemeProviderState
> {
  private readonly setThemeMode: (themeMode: PaletteMode) => PayloadAction<PaletteMode>;

  constructor(props: IThemeProviderStateProps & IThemeProviderDispatchProps) {
    super(props);

    this.setThemeMode = props.setThemeMode;

    this.initThemeProvider();

    this.state = { currentThemeMode: getInitialThemeMode() };
  }

  static getAppropriateStyles<T extends string | number | Record<string, unknown>>(
    themeMode: PaletteMode,
    lightModeCaseStyles: T,
    darkModeCaseStyles: T
  ): T {
    if (themeMode === 'light') {
      return lightModeCaseStyles;
    }

    if (themeMode === 'dark') {
      return darkModeCaseStyles;
    }

    return lightModeCaseStyles;
  }

  updateThemeMode = (themeMode: PaletteMode) => {
    this.setState({ currentThemeMode: themeMode });
    setThemeModeStylingMetadata(themeMode);
    this.setThemeMode(themeMode);
  };

  private initThemeProvider() {
    const targetThemeMode: PaletteMode = getInitialThemeMode();

    setThemeModeStylingMetadata(targetThemeMode);
  }

  render() {
    const { currentThemeMode } = this.state;
    const themeModeContext: IThemeModeContext = {
      currentThemeMode,
      updateThemeMode: this.updateThemeMode,
      themesAvailable: {
        light: lightTheme,
        dark: darkTheme,
      },

      getCurrentTheme() {
        return this.themesAvailable[this.currentThemeMode];
      },
    };

    const theme: Theme = themeModeContext.themesAvailable[currentThemeMode];

    return (
      <ThemeModeContext.Provider value={themeModeContext}>
        <MuiThemeProvider theme={theme}>
          <GlobalStyles styles={globalCustomStyles} />
          {this.props.children}
        </MuiThemeProvider>
      </ThemeModeContext.Provider>
    );
  }
}

const mapStateToProps = (state: RootState): IThemeProviderStateProps => ({
  currentThemeMode: uiToolkit.selectors.currentThemeMode(state),
});
const mapDispatchToProps = (dispatch: Dispatch): IThemeProviderDispatchProps => ({
  setThemeMode: (mode: PaletteMode) => dispatch(uiToolkit.actions.setCurrentThemeMode(mode)),
});

export default (
  connect(mapStateToProps, mapDispatchToProps) as InferableComponentEnhancerWithProps<
    IThemeProviderStateProps & IThemeProviderDispatchProps,
    { children?: ReactNode }
  >
)(ThemeProvider);
